import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import XDate from 'xdate'

import AppLayout from '../../components/app-layout/AppLayout'
import CardSurface from '../../components/card-surface/CardSurface'
import { useResidentTenancy } from '../../_hooks/use-resident-tenancy'
import {
  bookAmenity,
  getAmenityAvailability,
  getBuildingAmenities,
  startAmenityDeposit,
} from '../../_data/resident'
import { AmenitySlot, ResidentAmenity } from '../../_types/resident'
import {
  formatAmenityDays,
  formatAmenityDeposit,
  formatAmenityHours,
  formatBookingShape,
  formatSlotTime,
  groupSlotsByDay,
} from './helpers'
import {
  PageStack,
  PageHeader,
  PageTitle,
  PageSubtitle,
  QuietText,
  AmenityDescription,
  AmenityMetaRow,
  MetaChip,
  DepositTag,
  DayHeading,
  SlotGrid,
  SlotButton,
  BackLink,
  ErrorBanner,
} from './styled'

const AVAILABILITY_DAYS = 14

const isoDay = (offsetDays: number): string =>
  new XDate().addDays(offsetDays).toString('yyyy-MM-dd')

/**
 * The renter's booking page for one amenity (/amenities/:amenityId): the amenity
 * details plus a slot picker over the next two weeks. Tapping a slot books it
 * (the server holds it transactionally; a lost race surfaces inline and the slots
 * refresh). On success the renter returns to /amenities where the new booking
 * appears under "Your bookings". Keyed on the selected tenancy.
 *
 * @returns {JSX.Element}
 */
const AmenityBookingPage = (): JSX.Element => {
  const navigate = useNavigate()
  const { amenityId } = useParams<{ amenityId: string }>()
  const { selectedTenancy, selectedTenancyId, isLoadingTenancies } =
    useResidentTenancy()
  const [amenity, setAmenity] = useState<ResidentAmenity | null>(null)
  const [slots, setSlots] = useState<AmenitySlot[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [bookingStartTime, setBookingStartTime] = useState<number>(0)
  const [errorMessage, setErrorMessage] = useState<string>('')

  useEffect(() => {
    if (selectedTenancyId === '' || amenityId === undefined) {
      setAmenity(null)
      setSlots([])
      setIsLoading(false)
      return
    }

    let isMounted = true
    setIsLoading(true)
    setErrorMessage('')

    void Promise.all([
      getBuildingAmenities(selectedTenancyId),
      getAmenityAvailability(
        selectedTenancyId,
        amenityId,
        isoDay(0),
        isoDay(AVAILABILITY_DAYS),
      ),
    ]).then(([amenities, availability]) => {
      if (isMounted === false) {
        return
      }
      setAmenity(amenities.find(item => item.id === amenityId) ?? null)
      setSlots(availability)
      setIsLoading(false)
    })

    return () => {
      isMounted = false
    }
  }, [selectedTenancyId, amenityId])

  const refreshSlots = (): void => {
    if (selectedTenancyId === '' || amenityId === undefined) {
      return
    }
    void getAmenityAvailability(
      selectedTenancyId,
      amenityId,
      isoDay(0),
      isoDay(AVAILABILITY_DAYS),
    ).then(setSlots)
  }

  const handleBook = (startTime: number): void => {
    if (
      selectedTenancyId === '' ||
      amenityId === undefined ||
      amenity === null
    ) {
      return
    }
    setBookingStartTime(startTime)
    setErrorMessage('')

    if (amenity.isPaid === true) {
      void startAmenityDeposit(selectedTenancyId, amenityId, startTime).then(
        checkoutUrl => {
          if (checkoutUrl !== null) {
            // Leaving for Stripe — keep the slots disabled through the redirect.
            window.location.href = checkoutUrl
            return
          }
          setBookingStartTime(0)
          setErrorMessage(
            'We couldn’t start the deposit just now — please try again.',
          )
          refreshSlots()
        },
      )
      return
    }

    void bookAmenity(selectedTenancyId, amenityId, startTime).then(booking => {
      setBookingStartTime(0)
      if (booking !== null) {
        navigate('/amenities')
        return
      }
      setErrorMessage('That time was just taken — please pick another slot.')
      refreshSlots()
    })
  }

  const dayGroups = groupSlotsByDay(slots)
  const isResolving = isLoadingTenancies === true || isLoading === true

  return (
    <AppLayout contentMaxWidth="standard">
      <PageStack>
        <BackLink type="button" onClick={() => navigate('/amenities')}>
          ← Back to amenities
        </BackLink>

        {isResolving === true ? (
          <CardSurface padding="loose">
            <QuietText>Loading…</QuietText>
          </CardSurface>
        ) : null}

        {isResolving === false && selectedTenancy === null ? (
          <CardSurface padding="loose">
            <QuietText>
              You don&rsquo;t have a residence selected. Claim your invite to
              book amenities.
            </QuietText>
          </CardSurface>
        ) : null}

        {isResolving === false &&
        selectedTenancy !== null &&
        amenity === null ? (
          <CardSurface padding="loose">
            <QuietText>
              We couldn&rsquo;t find that amenity. It may have been removed.
            </QuietText>
          </CardSurface>
        ) : null}

        {isResolving === false && amenity !== null ? (
          <>
            <PageHeader>
              <PageTitle>{amenity.name}</PageTitle>
              <PageSubtitle>
                Pick a time to book — times shown in Eastern Time.
              </PageSubtitle>
            </PageHeader>

            <CardSurface padding="standard">
              {amenity.description !== '' ? (
                <AmenityDescription>{amenity.description}</AmenityDescription>
              ) : null}
              <AmenityMetaRow>
                <MetaChip>{formatBookingShape(amenity.bookingShape)}</MetaChip>
                <MetaChip>{formatAmenityHours(amenity)}</MetaChip>
                <MetaChip>{formatAmenityDays(amenity.daysOfWeek)}</MetaChip>
                <MetaChip>
                  {amenity.capacity === 1
                    ? '1 spot'
                    : `${amenity.capacity} spots`}
                </MetaChip>
                <DepositTag tone={amenity.isPaid === true ? 'paid' : 'free'}>
                  {formatAmenityDeposit(amenity)}
                </DepositTag>
              </AmenityMetaRow>
              {amenity.requiresApproval === true ? (
                <QuietText>
                  Your booking will be sent to the property manager for
                  approval.
                </QuietText>
              ) : null}
              {amenity.isPaid === true ? (
                <QuietText>
                  Booking places a refundable {formatAmenityDeposit(amenity)}{' '}
                  hold on your card. It&rsquo;s released after your booking
                  unless the property manager needs to keep it.
                </QuietText>
              ) : null}
            </CardSurface>

            {errorMessage !== '' ? (
              <ErrorBanner>{errorMessage}</ErrorBanner>
            ) : null}

            {dayGroups.length === 0 ? (
              <CardSurface padding="loose">
                <QuietText>
                  No open times in the next two weeks. Check back soon.
                </QuietText>
              </CardSurface>
            ) : (
              <CardSurface padding="standard">
                {dayGroups.map(group => (
                  <div key={group.dayKey}>
                    <DayHeading>{group.dayLabel}</DayHeading>
                    <SlotGrid>
                      {group.slots.map(slot => (
                        <SlotButton
                          key={slot.startTime}
                          type="button"
                          disabled={bookingStartTime !== 0}
                          onClick={() => handleBook(slot.startTime)}
                        >
                          {formatSlotTime(slot.startTime)}
                        </SlotButton>
                      ))}
                    </SlotGrid>
                  </div>
                ))}
              </CardSurface>
            )}
          </>
        ) : null}
      </PageStack>
    </AppLayout>
  )
}

export default AmenityBookingPage
